import React from 'react';
import Hero from '@/components/Hero';
import Features from '@/components/Features';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ShoppingCart, HeartHandshake, ChevronDown, Store, ShoppingBag } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useLanguage } from '@/contexts/LanguageContext';
import { THUISBEZORGD_ORDER_URL } from '@/components/OrderModal';

const AboutUsSection = () => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { t } = useLanguage();

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const ReadMoreButton = () => (
    <div className="text-center mt-8">
      <Button onClick={toggleExpanded} variant="outline" className="secondary-button">
        {isExpanded ? t('aboutUs.readLess') : t('aboutUs.readMore')}
        <ChevronDown className={`ml-2 h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </Button>
    </div>
  );

  return (
    <section id="over-ons" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <HeartHandshake className="h-16 w-16 brand-text mx-auto mb-6" />
          </motion.div>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-6 text-gray-800"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <span className="brand-text">{t('aboutUs.title')}</span>
          </motion.h2>
        </div>
        <motion.div
          className="max-w-3xl mx-auto space-y-6 text-lg text-muted-foreground text-left sm:text-justify"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <p>
            {t('aboutUs.paragraph1')}
          </p>

          {!isExpanded && <ReadMoreButton />}
          
          <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="space-y-6 pt-6">
              <p>
                {t('aboutUs.paragraph2')}
              </p>
              <p>
                {t('aboutUs.paragraph3')}
              </p>
              <p>
                {t('aboutUs.paragraph4')}
              </p>
              {isExpanded && <ReadMoreButton />}
            </div>
          </div>

          <p className="text-center text-xl font-semibold brand-text mt-10 pt-6 border-t border-gray-200">
            {t('aboutUs.closing')}
          </p>
          <p className="text-center text-lg text-gray-700">
            {t('aboutUs.farewell')}
          </p>
          <p className="text-center text-md text-muted-foreground italic mt-4">
            {t('aboutUs.signature')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};


const FoodShowcase = () => {
  const { t } = useLanguage();
  const dishes = [
    { 
      name: "Bara", 
      imageSrc: "/images/dishes/bara.jpg",
      imageAlt: "Bara",
    },
    { 
      name: "Diverse broodjes", 
      imageSrc: "/images/dishes/diverse-surinaamse-broodjes.jpg",
      imageAlt: "Diverse broodjes",
    },
    { 
      name: "Idly met chutney en sambar", 
      imageSrc: "/images/dishes/idly.jpg",
      imageAlt: "Idly met chutney en sambar",
    },
    { 
      name: "Vada met chutney en sambar", 
      imageSrc: "/images/dishes/vada.jpg",
      imageAlt: "Vada met chutney en sambar", 
    },
    { 
      name: "Roti kipfilet kerrie", 
      imageSrc: "/images/dishes/roti-kipfilet-kerrie.jpg",
      imageAlt: "Roti kipfilet kerrie",
    },
    {
      name: "Dosa met chutney en sambar",
      imageSrc: "/images/dishes/dosa.jpg",
      imageAlt: "Dosa met chutney en sambar",
    }
  ];

  return (
    <section className="py-20 md:py-28 section-bg-subtle pattern-bg">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.h2
            className="text-3xl md:text-4xl font-bold mb-6 text-gray-800"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t('foodShowcase.title')} <span className="brand-text">{t('foodShowcase.specialties')}</span>
          </motion.h2>
          <motion.p
            className="text-lg text-muted-foreground"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {t('foodShowcase.subtitle')}
          </motion.p>
        </div>
        
        <Carousel
          opts={{
            align: "start",
            loop: true,
          }}
          className="w-full max-w-xs sm:max-w-xl md:max-w-3xl lg:max-w-5xl mx-auto"
          aria-label={t('foodShowcase.title')}
        >
          <CarouselContent>
            {dishes.map((dish, index) => (
              <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                <motion.div
                  className="bg-card rounded-xl shadow-lg overflow-hidden card-hover border border-gray-100 h-full flex flex-col"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="aspect-video">
                    <img  className="w-full h-full object-cover" alt={dish.imageAlt} src={dish.imageSrc} />
                  </div>
                  <div className="p-6 flex-grow">
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">{dish.name}</h3>
                  </div>
                </motion.div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="flex -left-4 md:-left-8 lg:-left-12" aria-label="Vorige gerecht" />
          <CarouselNext className="flex -right-4 md:-right-8 lg:-right-12" aria-label="Volgende gerecht" />
        </Carousel>
      </div>
    </section>
  );
};

const CallToActionSection = () => {
  const { t } = useLanguage();
  const contactLink = "/contact";
  const marketDayLink = "/market-day";

  return (
    <section className="py-20 md:py-28 bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h2 
          className="text-3xl md:text-4xl font-bold mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {t('callToAction.title')}
        </motion.h2>
        <motion.p 
          className="text-lg md:text-xl mb-10 max-w-3xl mx-auto opacity-90"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {t('callToAction.subtitle')}
        </motion.p>
        <motion.div 
          className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Link to={contactLink} className="w-full sm:w-auto">
            <Button size="lg" variant="outline" className="bg-white text-primary hover:bg-gray-100 border-white hover:border-gray-100 w-full">
              <Store className="mr-2 h-5 w-5" /> {t('callToAction.shopButton')}
            </Button>
          </Link>
          <Button 
            asChild
            size="lg" 
            className="bg-amber-500 text-white hover:bg-amber-600 border-amber-500 hover:border-amber-600 w-full sm:w-auto"
          >
            <a href={THUISBEZORGD_ORDER_URL} target="_blank" rel="noopener noreferrer">
              <ShoppingCart className="mr-2 h-5 w-5" /> {t('callToAction.orderButton')}
            </a>
          </Button>
          <Link to={marketDayLink} className="w-full sm:w-auto">
            <Button size="lg" variant="outline" className="bg-white text-primary hover:bg-gray-100 border-white hover:border-gray-100 w-full">
              <ShoppingBag className="mr-2 h-5 w-5" /> {t('callToAction.marketButton')}
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};


const HomePage = () => {
  return (
    <>
      <Hero />
      <Features />
      <FoodShowcase />
      <AboutUsSection />
      <CallToActionSection />
    </>
  );
};

export default HomePage;